/**
 * Zustand store implementation.
 *
 * @module
 */
import { subscribeWithSelector } from 'zustand/middleware';
import { createStore as createZustandStore } from 'zustand/vanilla';
/**
 * Creates a Zustand-backed store that conforms to the molecule `Store` interface.
 * Uses `zustand/vanilla` with `subscribeWithSelector` middleware. Supports molecule middleware,
 * and optional persistence to `localStorage` (or a custom storage backend).
 * @param config - Store configuration including `initialState`, optional molecule `middleware`, and optional `persist` config with `name`, `storage`, and `partialize`.
 * @returns A molecule `Store` with `getState`, `setState`, `subscribe`, and `destroy`.
 */
export const createStore = (config) => {
    const { initialState, middleware: moleculeMiddleware, persist: persistConfig, } = config;
    // Create the Zustand store with subscribeWithSelector
    const zustandStore = createZustandStore()(subscribeWithSelector(() => initialState));
    // Wrap setState with molecule middleware
    let enhancedSetState = (partial) => {
        zustandStore.setState((state) => {
            const nextPartial = typeof partial === 'function' ? partial(state) : partial;
            return { ...state, ...nextPartial };
        });
    };
    // Apply molecule middleware
    if (moleculeMiddleware) {
        for (let i = moleculeMiddleware.length - 1; i >= 0; i--) {
            enhancedSetState = moleculeMiddleware[i](enhancedSetState, zustandStore.getState);
        }
    }
    // Handle persistence
    if (persistConfig) {
        const storage = persistConfig.storage ?? (typeof localStorage !== 'undefined' ? localStorage : null);
        if (storage) {
            // Load initial state from storage
            try {
                const stored = storage.getItem(persistConfig.name);
                if (stored) {
                    const parsed = JSON.parse(stored);
                    zustandStore.setState((state) => ({ ...state, ...parsed }));
                }
            }
            catch (_error) {
                // Ignore: storage may be unavailable (private browsing, quota exceeded, etc.) — safe to start with in-memory state.
            }
            // Subscribe to changes and persist
            zustandStore.subscribe((state) => {
                try {
                    const toPersist = persistConfig.partialize ? persistConfig.partialize(state) : state;
                    storage.setItem(persistConfig.name, JSON.stringify(toPersist));
                }
                catch (_error) {
                    // Ignore: write failures (quota, serialization errors) do not corrupt in-memory state — persistence is best-effort.
                }
            });
        }
    }
    return {
        getState: zustandStore.getState,
        setState: enhancedSetState,
        subscribe: (listener) => {
            return zustandStore.subscribe((state, prevState) => {
                listener(state, prevState);
            });
        },
        destroy: () => {
            // Zustand v5 stores no longer expose a destroy method.
            // Subscribe returns an unsubscribe function per listener;
            // callers should unsubscribe individually if needed.
        },
    };
};
//# sourceMappingURL=store.js.map